import React from 'react';

import { DEFAULT_FORM_STATE, defaultOptions } from './logic/createFormControl';
import getProxyFormState from './logic/getProxyFormState';
import cloneObject from './utils/cloneObject';
import deepEqual from './utils/deepEqual';
import isFunction from './utils/isFunction';
import { FORM_ERROR_TYPE } from './constants';
import { createFormControl } from './logic';
import type {
  FieldValues,
  FormState,
  UseFormProps,
  UseFormReturn,
} from './types';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';
import { useResyncOnReconnect } from './useResyncOnReconnect';

const RESET_OPTIONS = {
  mode: defaultOptions.mode,
  reValidateMode: defaultOptions.reValidateMode,
  shouldFocusError: defaultOptions.shouldFocusError,
  resolver: undefined,
  criteriaMode: undefined,
  delayError: undefined,
  shouldUnregister: undefined,
  shouldUseNativeValidation: undefined,
  context: undefined,
} as const;

/**
 * Core hook for managing a form. Returns all methods and state for
 * registration, validation, and submission.
 *
 * @see [API](https://react-hook-form.com/docs/useform)
 *
 * @example
 * ```tsx
 * const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();
 * <form onSubmit={handleSubmit(onSubmit)}>
 *   <input {...register("email", { required: true })} />
 * </form>
 * ```
 */
export function useForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
>(
  props: UseFormProps<TFieldValues, TContext, TTransformedValues> = {},
): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  const _formControl = React.useRef<
    UseFormReturn<TFieldValues, TContext, TTransformedValues> | undefined
  >(undefined);
  const _values = React.useRef<typeof props.values>(undefined);
  const _formControlProp = React.useRef(props.formControl);
  const _hadValidate = React.useRef(!!props.validate);
  const [formState, updateFormState] = React.useState<FormState<TFieldValues>>(
    () => ({
      ...cloneObject(DEFAULT_FORM_STATE),
      isLoading: isFunction(props.defaultValues),
      errors: props.errors || {},
      disabled: props.disabled || false,
      defaultValues: isFunction(props.defaultValues)
        ? undefined
        : props.defaultValues,
    }),
  );

  if (
    !_formControl.current ||
    (props.formControl && _formControlProp.current !== props.formControl)
  ) {
    _formControlProp.current = props.formControl;
    if (props.formControl) {
      _formControl.current = {
        ...props.formControl,
        formState,
      };

      if (props.defaultValues && !isFunction(props.defaultValues)) {
        props.formControl.reset(props.defaultValues, props.resetOptions);
      }
    } else {
      const { formControl, ...rest } = createFormControl(props);

      _formControl.current = {
        ...rest,
        formState,
      };
    }
  }

  const control = _formControl.current.control;

  const previousOptions = control._options;
  const removedOptions: Record<string, unknown> = {};

  for (const key in RESET_OPTIONS) {
    if (key in previousOptions && !(key in props)) {
      removedOptions[key] = RESET_OPTIONS[key as keyof typeof RESET_OPTIONS];
    }
  }

  control._options = {
    ...removedOptions,
    ...props,
    validate: props.validate,
  };

  const getCurrentFormState = () => ({
    ...control._formState,
    defaultValues:
      control._defaultValues as FormState<TFieldValues>['defaultValues'],
  });

  const { resyncIfNeeded, snapshot } =
    useResyncOnReconnect<FormState<TFieldValues>>(getCurrentFormState);

  useIsomorphicLayoutEffect(() => {
    resyncIfNeeded(true, getCurrentFormState, updateFormState);

    const unsubscribe = control._subscribe({
      formState: control._proxyFormState,
      callback: () =>
        updateFormState({
          ...control._formState,
          defaultValues:
            control._defaultValues as FormState<TFieldValues>['defaultValues'],
        }),
      reRenderRoot: true,
    });

    updateFormState((data) => ({
      ...data,
      isReady: true,
    }));

    control._formState.isReady = true;

    return () => {
      unsubscribe();
      snapshot(true, getCurrentFormState);
    };
  }, [control, resyncIfNeeded, snapshot]);

  React.useEffect(
    () => control._disableForm(props.disabled),
    [control, props.disabled],
  );

  React.useEffect(() => {
    if (props.mode) {
      control._options.mode = props.mode;
    }
    if (props.reValidateMode) {
      control._options.reValidateMode = props.reValidateMode;
    }
  }, [control, props.mode, props.reValidateMode]);

  React.useEffect(() => {
    if (props.errors) {
      control._setErrors(props.errors);
      control._focusError();
    }
  }, [control, props.errors]);

  React.useEffect(() => {
    if (_hadValidate.current && !props.validate) {
      _formControl.current && _formControl.current.clearErrors(FORM_ERROR_TYPE);
    }
    _hadValidate.current = !!props.validate;
  }, [props.validate]);

  React.useEffect(() => {
    props.shouldUnregister &&
      control._subjects.state.next({
        values: control._getWatch(),
      });
  }, [control, props.shouldUnregister]);

  React.useEffect(() => {
    if (control._proxyFormState.isDirty) {
      const isDirty = control._getDirty();
      if (isDirty !== formState.isDirty) {
        control._subjects.state.next({
          isDirty,
        });
      }
    }
  }, [control, formState.isDirty]);

  React.useEffect(() => {
    if (props.values && !deepEqual(props.values, _values.current)) {
      control._reset(props.values, {
        keepFieldsRef: true,
        ...control._options.resetOptions,
      });
      if (
        !(
          control._options.resetOptions &&
          control._options.resetOptions.keepIsValid
        )
      ) {
        control._setValid();
      }
      _values.current = props.values;
      updateFormState((state) => ({ ...state }));
    } else {
      control._resetDefaultValues();
    }
  }, [control, props.values]);

  React.useEffect(() => {
    if (!control._state.mount) {
      control._setValid();
      control._state.mount = true;
    }

    if (control._state.watch) {
      control._state.watch = false;
      control._subjects.state.next({ ...control._formState });
    }

    control._removeUnmounted();
  });

  _formControl.current.formState = React.useMemo(
    () => getProxyFormState(formState, control),
    [control, formState],
  );

  return _formControl.current;
}
